import { useConnections } from '@integration-app/react';
import {
  EnvelopeSimple,
  Phone,
  Plus,
  SlidersHorizontal,
  Trash,
  ChatCircle,
} from '@phosphor-icons/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AutoCompleteMultiSelectAgentNumber } from 'src/components/AutoCompleteMultiSelectAgentNumber';
import { AutoCompleteSelectBox } from 'src/components/AutoCompleteSelectBox';
import { Button } from 'src/components/Button';
import { CardWithBorder } from 'src/components/CardWithBorder';
import { Input } from 'src/components/Input';
import { Label } from 'src/components/Label';
import { Loading } from 'src/components/Loading';
import { Option, SelectBox } from 'src/components/SelectBox';
import { Textarea } from 'src/components/Textarea';
import useAuthedRequest from 'src/hooks/useAuthedRequest';
import { Can, Subject } from 'src/hooks/usePermissions';
import { Agent } from 'src/interfaces/agent.interface';
import { QueryObject } from 'src/interfaces/queryObject.interface';
import { DaySelector } from '../ui/DaySelector';
import { EmailFields } from '../ui/email/EmailFields';
import { EmailProviderSelect } from '../ui/email/EmailProviderSelect';
import { SmsProviderSelect } from '../ui/sms/SmsProviderSelect';
import { SmsFields } from '../ui/sms/SmsFields';
import { CrmProviderSelect } from '../ui/CrmProviderSelect';
import {
  AGENT_OPTION_SAME_AS_INITIAL_CALL,
  DEFAULT_CRM_PROVIDER_OPTION,
  FollowUpCommunicationForm,
  generateListElementId,
  getTodayLocal,
  initialCallAfterOptions,
  timeZoneOptions,
  useWizard,
} from './WizardContext';
import { CommunicationChannel } from 'src/services/campaigns/campaigns.interface';
import { WarningPanel } from 'src/components/WarningPanel';
import { DayOfWeek } from 'src/interfaces/workflows.interface';
import { IntegrationKey } from 'src/services/integration-app.service';

const validateCallTimes = (
  earliestTime: string,
  latestTime: string,
  days: DayOfWeek[],
) => {
  if (!earliestTime || !latestTime) return false;

  const earliestHour = parseInt(earliestTime.split(':')[0]);
  const latestTime24 = latestTime.split(':');
  const latestHour = parseInt(latestTime24[0]);
  const latestMinutes = parseInt(latestTime24[1]);

  // See restrictions by state:
  // https://a-us.storyblok.com/f/1020997/x/d026cda979/state-calling-restrictions_april_2024.pdf

  // Check if earliest time is before 9 AM or latest time is after 8 PM.
  const isEarlyViolation = earliestHour < 9;
  const isLateViolation =
    latestHour > 20 || (latestHour === 20 && latestMinutes > 0);

  // Check for day of the week violations.
  const isSundayViolation = days.includes(DayOfWeek.sunday);

  return isEarlyViolation || isLateViolation || isSundayViolation;
};

const nonAutoSyncableProviders: string[] = [
  IntegrationKey.MONDAY,
  IntegrationKey.SALESFORCE_PARDOT,
];

export function ConfigureAutomation() {
  const communicationSettings = {
    [CommunicationChannel.SMS]: {
      icon: <ChatCircle className="h-6 w-6 text-primary mr-2" />,
      label: 'SMS',
    },
    [CommunicationChannel.EMAIL]: {
      icon: <EnvelopeSimple className="h-6 w-6 text-primary mr-2" />,
      label: 'Email',
    },
    [CommunicationChannel.CALL]: {
      icon: <Phone className="h-6 w-6 text-primary mr-2" />,
      label: 'Call',
    },
  };

  const {
    isInitializing,
    saveCampaignAutomation,
    nextStep,
    resetWizard,
    campaignAutomation,
    setCampaignAutomation,
    isEditing,
  } = useWizard();
  const { get } = useAuthedRequest();
  const [loadingAgents, setLoadingAgents] = useState(false);
  const [agentOptions, setAgentOptions] = useState<Option[]>([]);
  const [customReferenceId, setCustomReferenceId] = useState(false);
  const [enableCrmIntegration, setEnableCrmIntegration] = useState(false);
  const [showScheduleWarning, setShowScheduleWarning] = useState(false);
  const [scheduleWarningAccepted, setScheduleWarningAccepted] = useState(false);
  const { connections: integrationAppConnections } = useConnections();
  const [savePending, setSavePending] = useState(false);
  const navigate = useNavigate();
  const hasInitializedCrmIntegration = useRef(false);

  useEffect(() => {
    if (!isInitializing && isEditing && !hasInitializedCrmIntegration.current) {
      hasInitializedCrmIntegration.current = true;
      if (campaignAutomation.crmProviderOption.value) {
        setEnableCrmIntegration(true);
      }
    }
  }, [isInitializing, isEditing, campaignAutomation.crmProviderOption.value]);

  const isCrmProviderAuthenticated = useMemo(() => {
    const connection =
      campaignAutomation.crmProviderOption.value &&
      integrationAppConnections.find(
        (connection) =>
          connection.integration?.key ===
          campaignAutomation.crmProviderOption.value,
      );

    return connection && !connection?.disconnected;
  }, [integrationAppConnections, campaignAutomation.crmProviderOption.value]);

  const isValidEmailConfig = useMemo(() => {
    const emailFollowUps = campaignAutomation.followUpCommunications.filter(
      (comm) => comm.contactVia === CommunicationChannel.EMAIL,
    );

    if (emailFollowUps.length === 0) return true;

    // Check if campaign-level email provider is set and authenticated
    if (!campaignAutomation.emailProviderOption?.value) return false;

    const connection = integrationAppConnections.find(
      (conn) =>
        conn.integration?.key ===
          campaignAutomation.emailProviderOption?.value && !conn.disconnected,
    );

    return !!connection;
  }, [
    integrationAppConnections,
    campaignAutomation.emailProviderOption,
    campaignAutomation.followUpCommunications,
  ]);

  const isValidSmsConfig = useMemo(() => {
    const smsFollowUps = campaignAutomation.followUpCommunications.filter(
      (comm) => comm.contactVia === CommunicationChannel.SMS,
    );

    if (smsFollowUps.length === 0) return true;

    // Check if campaign-level SMS provider is set and authenticated
    if (!campaignAutomation.smsProviderOption?.value) return false;

    const connection = integrationAppConnections.find(
      (conn) =>
        conn.integration?.key === campaignAutomation.smsProviderOption?.value &&
        !conn.disconnected,
    );

    return !!connection;
  }, [
    integrationAppConnections,
    campaignAutomation.smsProviderOption,
    campaignAutomation.followUpCommunications,
  ]);

  const saveDisabled =
    savePending ||
    campaignAutomation.name === '' ||
    campaignAutomation.referenceId === '' ||
    campaignAutomation.fromNumberOptions.length === 0 ||
    !campaignAutomation.agentOption ||
    campaignAutomation.schedule.campaignStartDate === '' ||
    campaignAutomation.schedule.days.length === 0 ||
    campaignAutomation.schedule.startTime === '' ||
    campaignAutomation.schedule.endTime === '' ||
    (enableCrmIntegration &&
      (!campaignAutomation.crmProviderOption.value ||
        !isCrmProviderAuthenticated)) ||
    !isValidEmailConfig ||
    !isValidSmsConfig ||
    (showScheduleWarning && !scheduleWarningAccepted);

  // Update warning state when call times change
  useEffect(() => {
    const shouldShowWarning = validateCallTimes(
      campaignAutomation.schedule.startTime,
      campaignAutomation.schedule.endTime,
      campaignAutomation.schedule.days,
    );

    setShowScheduleWarning(shouldShowWarning);
  }, [
    campaignAutomation.schedule.startTime,
    campaignAutomation.schedule.endTime,
    campaignAutomation.schedule.days,
  ]);

  const fetchAgentOptions = useCallback(
    async (name: string) => {
      try {
        setLoadingAgents(true);
        const queryObject: QueryObject = {
          ...(name.length !== 0 && { filters: JSON.stringify({ name }) }),
        };
        const query = new URLSearchParams(
          queryObject as Record<string, string>,
        );

        const data = await get(`/agents?${query.toString()}`);

        const options = data.items.map((agent: Agent) => ({
          value: agent.id,
          label: agent.name,
        }));
        setAgentOptions(options);
        return options;
      } finally {
        setLoadingAgents(false);
      }
    },
    [get],
  );

  const fetchFollowupCallAgentOptions = useCallback(
    async (name: string) => {
      const fetchedAgentOptions = await fetchAgentOptions(name);
      const result = [...fetchedAgentOptions];
      if (
        !name ||
        AGENT_OPTION_SAME_AS_INITIAL_CALL.label!.toLowerCase().includes(
          name.toLowerCase(),
        )
      ) {
        result.unshift(AGENT_OPTION_SAME_AS_INITIAL_CALL);
      }
      return result;
    },
    [fetchAgentOptions],
  );

  const handleSubmit = async () => {
    setSavePending(true);
    try {
      const automationCreated = await saveCampaignAutomation();

      if (automationCreated) {
        nextStep();
      }
    } finally {
      setSavePending(false);
    }
  };

  const handleCancel = () => {
    resetWizard();
    navigate('/campaigns');
  };

  const normalizeReferenceId = (value: string) => {
    return value
      .replace(/\s+/g, '-')
      .toLowerCase()
      .replace(/[^a-z0-9-]/g, '');
  };

  const createReferenceId = (value: string) => {
    if (!value) {
      return '';
    }
    return `${normalizeReferenceId(value)}-${getTodayLocal()}`;
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const name = e.target.value;
    let updatedAutomation = { ...campaignAutomation, name };
    if (!customReferenceId && !isEditing) {
      const referenceId = createReferenceId(name);
      updatedAutomation = { ...updatedAutomation, referenceId };
    }
    setCampaignAutomation(updatedAutomation);
  };

  const handleReferenceIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCustomReferenceId(true);
    const referenceId = normalizeReferenceId(e.target.value);
    setCampaignAutomation({ ...campaignAutomation, referenceId });
  };

  const handleEmailProviderChange = (providerOption: {
    value: string;
    label: string;
  }) => {
    setCampaignAutomation({
      ...campaignAutomation,
      emailProviderOption: providerOption,
    });
  };

  const handleSmsProviderChange = (providerOption: {
    value: string;
    label: string;
  }) => {
    setCampaignAutomation({
      ...campaignAutomation,
      smsProviderOption: providerOption,
    });
  };

  if (isInitializing) {
    return (
      <div className="flex gap-2 items-center justify-center">
        <Loading size="8" />
        <p className="text-neutral-400">Loading...</p>
      </div>
    );
  }

  return (
    <>
      <h3 className="mb-4">Configure Campaign</h3>
      <p className="mb-4">Set up the details for your campaign.</p>

      <div className="flex flex-col">
        <div className="mb-6">
          <Input
            id="automation-name"
            label="Campaign Name"
            type="text"
            placeholder="Enter campaign name"
            value={campaignAutomation.name ?? ''}
            onChange={handleNameChange}
            className="max-w-xl"
            required={true}
          />
        </div>
        <div className="mb-6">
          <Input
            id="reference-id"
            label="Campaign ID"
            type="text"
            placeholder="Enter campaign ID"
            value={campaignAutomation.referenceId ?? ''}
            onChange={handleReferenceIdChange}
            className="max-w-xl"
            required={true}
            disabled={isEditing}
          />
        </div>
        <div className="mb-6 col-span-2">
          <Label htmlFor="description">Description</Label>
          <Textarea
            id="description"
            placeholder="Enter description"
            rows={2}
            value={campaignAutomation.description}
            onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
              setCampaignAutomation({
                ...campaignAutomation,
                description: event.target.value,
              });
            }}
            className="max-w-xl"
          />
        </div>
      </div>

      <div className="border-t border-neutral-200 pt-6">
        <h3 className="mb-6">Schedule</h3>

        <div className="flex flex-col">
          <div className="mb-6">
            <Input
              id="campaign-start-date"
              label="Campaign Start Date"
              type="date"
              value={campaignAutomation.schedule.campaignStartDate}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setCampaignAutomation({
                  ...campaignAutomation,
                  schedule: {
                    ...campaignAutomation.schedule,
                    campaignStartDate: e.target.value,
                  },
                });
              }}
              className="max-w-xs"
              required={true}
            />
          </div>

          <div className="mb-6">
            <Label htmlFor="days">Days</Label>
            <div className="flex flex-wrap gap-2">
              <DaySelector
                value={campaignAutomation.schedule.days}
                setValue={(days) => {
                  setCampaignAutomation({
                    ...campaignAutomation,
                    schedule: {
                      ...campaignAutomation.schedule,
                      days: days as DayOfWeek[],
                    },
                  });
                }}
              />
            </div>
          </div>

          <div className="flex flex-col sm:flex-row sm:flex-wrap gap-y-6 sm:gap-x-8 mb-6">
            <div className="w-full sm:w-80 flex-shrink-0">
              <Input
                id="time-start"
                label="Earliest Call Time"
                type="time"
                value={campaignAutomation.schedule.startTime}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setCampaignAutomation({
                    ...campaignAutomation,
                    schedule: {
                      ...campaignAutomation.schedule,
                      startTime: e.target.value,
                    },
                  });
                }}
                className="w-full max-w-xs"
                required={true}
              />
            </div>

            <div className="w-full sm:w-80 flex-shrink-0">
              <Input
                id="time-end"
                label="Latest Call Time"
                type="time"
                value={campaignAutomation.schedule.endTime}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setCampaignAutomation({
                    ...campaignAutomation,
                    schedule: {
                      ...campaignAutomation.schedule,
                      endTime: e.target.value,
                    },
                  });
                }}
                className="w-full max-w-xs"
                required={true}
              />
            </div>

            <div className="w-full sm:w-80 flex-shrink-0">
              <Label htmlFor="time-zone">Time Zone</Label>
              <SelectBox
                options={timeZoneOptions}
                defaultValue={campaignAutomation.schedule.timeZoneOption}
                variant="outlined"
                color="primary"
                size="medium"
                className="w-full max-w-xs"
                onChange={(value) => {
                  const option = timeZoneOptions.find(
                    (item) => item.value === value,
                  )!;
                  setCampaignAutomation({
                    ...campaignAutomation,
                    schedule: {
                      ...campaignAutomation.schedule,
                      timeZoneOption: option,
                    },
                  });
                }}
              />
            </div>
          </div>

          {/* Conditional Warning Message */}
          <WarningPanel
            show={showScheduleWarning}
            title="Call time outside legal hours"
            acknowledgement={{
              checked: scheduleWarningAccepted,
              onChange: (checked) => setScheduleWarningAccepted(checked),
              label:
                'I acknowledge that placing calls outside permitted hours may result in legal penalties.',
            }}
            className="mb-6"
          >
            The call time you chose falls outside the legally permitted window
            for consumer sales calls &mdash; typically 9 a.m. to 8 p.m., Monday
            through Saturday, though exact limits vary by state. Placing a call
            at this time could subject your company to significant fines.
          </WarningPanel>
        </div>
      </div>

      <div className="border-t border-neutral-200 pt-6 mb-6">
        <h3 className="mb-6">Communication</h3>

        {/* Initial Call */}
        <CardWithBorder>
          <div className="flex items-center mb-4">
            <Phone className="h-6 w-6 text-primary mr-2" />
            <h4>Initial Call</h4>
          </div>

          <div className="flex flex-col sm:flex-row sm:flex-wrap gap-y-6 sm:gap-x-8 mb-6">
            <div className="w-full sm:w-80 flex-shrink-0">
              <Label htmlFor="initial-call-after">When</Label>
              <SelectBox
                options={initialCallAfterOptions}
                defaultValue={campaignAutomation.initialCallAfterOption}
                variant="outlined"
                color="primary"
                size="medium"
                className="w-full"
                onChange={(value) => {
                  const option = initialCallAfterOptions.find(
                    (item) => item.value === value,
                  )!;
                  setCampaignAutomation({
                    ...campaignAutomation,
                    initialCallAfterOption: option,
                  });
                }}
              />
              <p className="text-sm text-neutral-400 mt-1">
                When to place the first call after the contact is added to the
                campaign.
              </p>
            </div>

            <div className="w-full sm:w-80 flex-shrink-0">
              <Label required htmlFor="agent">
                Agent
              </Label>
              <AutoCompleteSelectBox
                defaultValue={campaignAutomation.agentOption}
                fetchOptions={fetchAgentOptions}
                onChange={(value) => {
                  const option = agentOptions.find(
                    (item) => item.value === value,
                  )!;
                  setCampaignAutomation({
                    ...campaignAutomation,
                    agentOption: option,
                  });
                }}
                variant="outlined"
                color="primary"
                size="medium"
                disabled={loadingAgents}
                className="w-full max-w-xs"
              />
            </div>

            <div className="w-full sm:w-80 flex-shrink-0">
              <Label required htmlFor="from-numbers">
                From Numbers
              </Label>
              <AutoCompleteMultiSelectAgentNumber
                selectedOptions={campaignAutomation.fromNumberOptions}
                onSelectionChange={(options) => {
                  setCampaignAutomation({
                    ...campaignAutomation,
                    fromNumberOptions: options,
                  });
                }}
                placeholder="Select numbers or tags..."
                noOptionsText="No matching numbers or tags found"
                variant="outlined"
                color="primary"
                size="medium"
                className="w-full max-w-xs"
              />
            </div>
          </div>
        </CardWithBorder>

        {/* Follow-up Communication Header */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <h4>Follow-up Communication</h4>
            <Button
              type="button"
              variant="outlined"
              size="small"
              onClick={() => {
                setCampaignAutomation({
                  ...campaignAutomation,
                  followUpCommunications: [
                    ...(campaignAutomation.followUpCommunications || []),
                    {
                      id: generateListElementId(),
                      daysAfter: 1,
                      contactVia: CommunicationChannel.CALL,
                      agentOption: AGENT_OPTION_SAME_AS_INITIAL_CALL,
                    },
                  ],
                });
              }}
              className="flex items-center"
            >
              <Plus className="mr-1 h-4 w-4" />
              Add Follow-up
            </Button>
          </div>
          <p className="text-sm text-neutral-400 mb-4">
            We send follow-up communication when we are unsure if the contact
            has been reached.
          </p>
        </div>

        {/* Follow-up Communications */}
        {!campaignAutomation.followUpCommunications ||
        campaignAutomation.followUpCommunications.length === 0 ? (
          <div className="p-4 border border-dashed border-neutral-200 rounded-md text-center text-neutral-400">
            No follow-ups scheduled. Click "Add Follow-up" to schedule a
            follow-up.
          </div>
        ) : (
          <div className="space-y-4">
            {campaignAutomation.followUpCommunications.map(
              (
                followUpCall: FollowUpCommunicationForm,
                followUpCallIndex: number,
              ) => (
                <div
                  key={followUpCall.id}
                  className="p-4 border border-neutral-200 rounded-md bg-neutral-50"
                >
                  <div className="flex justify-between items-center mb-3">
                    <div className="flex items-center">
                      {communicationSettings[followUpCall.contactVia]?.icon}
                      <span className="text-sm font-medium text-neutral-600">
                        Follow-up {followUpCallIndex + 1} -{' '}
                        {communicationSettings[followUpCall.contactVia]?.label}
                      </span>
                    </div>
                    <Button
                      type="button"
                      variant="text"
                      size="small"
                      onClick={() => {
                        const newFollowUpCalls = [
                          ...campaignAutomation.followUpCommunications,
                        ];
                        newFollowUpCalls.splice(followUpCallIndex, 1);
                        setCampaignAutomation({
                          ...campaignAutomation,
                          followUpCommunications: newFollowUpCalls,
                        });
                      }}
                      className="text-neutral-400 hover:text-error"
                    >
                      <Trash className="h-4 w-4" />
                    </Button>
                  </div>

                  <div className="flex flex-col sm:flex-row sm:flex-wrap gap-y-6 sm:gap-x-8 mb-6">
                    <div className="w-full sm:w-80 flex-shrink-0">
                      <Label htmlFor={`follow-up-type-${followUpCallIndex}`}>
                        Contact Via
                      </Label>
                      <SelectBox
                        options={Object.values(CommunicationChannel).map(
                          (channel) => ({
                            value: channel,
                            label: communicationSettings[channel].label,
                          }),
                        )}
                        defaultValue={{
                          value: followUpCall.contactVia,
                          label:
                            communicationSettings[followUpCall.contactVia]
                              ?.label,
                        }}
                        variant="outlined"
                        color="primary"
                        size="medium"
                        className="w-full bg-neutral-0"
                        onChange={(value) => {
                          const newFollowUpCalls = [
                            ...campaignAutomation.followUpCommunications,
                          ];

                          let updatedCall: FollowUpCommunicationForm;
                          if (
                            value === CommunicationChannel.EMAIL &&
                            followUpCall.contactVia !==
                              CommunicationChannel.EMAIL
                          ) {
                            updatedCall = {
                              ...followUpCall,
                              contactVia: value as CommunicationChannel,
                              emailBody: '',
                              emailSubject: '',
                            };
                          } else if (
                            value === CommunicationChannel.SMS &&
                            followUpCall.contactVia !== CommunicationChannel.SMS
                          ) {
                            updatedCall = {
                              ...followUpCall,
                              contactVia: value as CommunicationChannel,
                              smsBody: '',
                            };
                          } else {
                            updatedCall = {
                              ...followUpCall,
                              contactVia: value as CommunicationChannel,
                            };
                          }

                          // Ensure calls have at least 1 day after
                          if (
                            updatedCall.contactVia ===
                              CommunicationChannel.CALL &&
                            (updatedCall.daysAfter === undefined ||
                              updatedCall.daysAfter < 1)
                          ) {
                            updatedCall = { ...updatedCall, daysAfter: 1 };
                          }

                          newFollowUpCalls[followUpCallIndex] = updatedCall;

                          setCampaignAutomation({
                            ...campaignAutomation,
                            followUpCommunications: newFollowUpCalls,
                          });
                        }}
                      />
                    </div>

                    <div className="w-full sm:w-80 flex-shrink-0">
                      <Label htmlFor={`follow-up-days-${followUpCallIndex}`}>
                        When
                      </Label>
                      <div className="flex items-center gap-2">
                        <Input
                          key={`${followUpCall.id}-${followUpCall.contactVia}`}
                          type="number"
                          min={
                            // We don't want contacts to get back-to-back calls.
                            followUpCall.contactVia ===
                            CommunicationChannel.CALL
                              ? '1'
                              : '0'
                          }
                          max="365"
                          className="w-20 flex-shrink-0"
                          fullWidth={false}
                          value={
                            followUpCall.daysAfter !== undefined
                              ? String(followUpCall.daysAfter)
                              : ''
                          }
                          onChange={(e) => {
                            let daysAfter: number | undefined = parseInt(
                              e.target.value,
                            );
                            if (isNaN(daysAfter)) {
                              daysAfter = undefined;
                            }
                            // Enforce channel-specific minimums
                            if (
                              daysAfter !== undefined &&
                              followUpCall.contactVia ===
                                CommunicationChannel.CALL &&
                              daysAfter < 1
                            ) {
                              daysAfter = 1;
                            }
                            if (
                              daysAfter !== undefined &&
                              followUpCall.contactVia !==
                                CommunicationChannel.CALL &&
                              daysAfter < 0
                            ) {
                              daysAfter = 0;
                            }
                            const newFollowUpCalls = [
                              ...campaignAutomation.followUpCommunications,
                            ];
                            newFollowUpCalls[followUpCallIndex] = {
                              ...followUpCall,
                              daysAfter,
                            };
                            setCampaignAutomation({
                              ...campaignAutomation,
                              followUpCommunications: newFollowUpCalls,
                            });
                          }}
                        />
                        <span className="text-neutral-600 flex-grow">
                          day(s) after initial call
                        </span>
                      </div>
                    </div>

                    {followUpCall.contactVia === CommunicationChannel.CALL && (
                      <div className="w-full sm:w-80 flex-shrink-0">
                        <Label htmlFor={`follow-up-agent-${followUpCallIndex}`}>
                          Agent
                        </Label>
                        <AutoCompleteSelectBox
                          defaultValue={followUpCall.agentOption}
                          fetchOptions={fetchFollowupCallAgentOptions}
                          onChange={(value) => {
                            const agentOption = agentOptions.find(
                              (item) => item.value === value,
                            )!;
                            const newFollowUpCalls = [
                              ...campaignAutomation.followUpCommunications,
                            ];
                            newFollowUpCalls[followUpCallIndex] = {
                              ...followUpCall,
                              agentOption,
                            };
                            setCampaignAutomation({
                              ...campaignAutomation,
                              followUpCommunications: newFollowUpCalls,
                            });
                          }}
                          variant="outlined"
                          color="primary"
                          size="medium"
                          disabled={loadingAgents}
                          className="w-full max-w-xs"
                        />
                      </div>
                    )}

                    {followUpCall.contactVia === CommunicationChannel.EMAIL && (
                      <EmailFields
                        followUpCall={followUpCall}
                        followUpCallIndex={followUpCallIndex}
                        onChange={(index, updatedCall) => {
                          const newFollowUpCalls = [
                            ...campaignAutomation.followUpCommunications,
                          ];
                          newFollowUpCalls[index] = {
                            ...followUpCall,
                            ...updatedCall,
                          };
                          setCampaignAutomation({
                            ...campaignAutomation,
                            followUpCommunications: newFollowUpCalls,
                          });
                        }}
                      />
                    )}

                    {followUpCall.contactVia === CommunicationChannel.SMS && (
                      <SmsFields
                        followUpCall={followUpCall}
                        followUpCallIndex={followUpCallIndex}
                        onChange={(index, updatedCall) => {
                          const newFollowUpCalls = [
                            ...campaignAutomation.followUpCommunications,
                          ];
                          newFollowUpCalls[index] = {
                            ...followUpCall,
                            ...updatedCall,
                          };
                          setCampaignAutomation({
                            ...campaignAutomation,
                            followUpCommunications: newFollowUpCalls,
                          });
                        }}
                      />
                    )}
                  </div>
                </div>
              ),
            )}
          </div>
        )}
      </div>

      {/* CRM Integration Section */}
      <div className="border-t border-neutral-200 pt-6 mb-6">
        <div className="mb-4">
          <div className="flex items-center justify-between">
            <div>
              <h3>
                CRM Integration{' '}
                <span className="text-xs text-neutral-400 ml-2">
                  (Optional)
                </span>
              </h3>
              <p className="text-sm text-neutral-400 mt-1">
                Set up CRM integration to automatically sync contacts into this
                campaign.
              </p>
              {nonAutoSyncableProviders.includes(
                campaignAutomation.crmProviderOption.value,
              ) && (
                <p className="text-sm text-warning-500 mt-1">
                  Automatic sync is currently unavailable for{' '}
                  {campaignAutomation.crmProviderOption.label}.
                </p>
              )}
            </div>
            {!enableCrmIntegration ? (
              <Button
                type="button"
                variant="outlined"
                size="small"
                onClick={() => setEnableCrmIntegration(true)}
                className="flex items-center"
              >
                <Plus className="mr-1 h-4 w-4" />
                Set Up CRM Integration
              </Button>
            ) : (
              <Button
                type="button"
                variant="text"
                size="small"
                onClick={() => {
                  setEnableCrmIntegration(false);
                  setCampaignAutomation({
                    ...campaignAutomation,
                    crmProviderOption: DEFAULT_CRM_PROVIDER_OPTION,
                    recordCommunicationsInCrm: false,
                  });
                }}
                className="text-error hover:text-error-500"
              >
                <Trash className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
        {enableCrmIntegration && (
          <>
            <CrmProviderSelect
              onChange={(providerOption) => {
                setCampaignAutomation({
                  ...campaignAutomation,
                  crmProviderOption: providerOption,
                });
              }}
              selectedProvider={campaignAutomation.crmProviderOption}
              isAuthenticated={!!isCrmProviderAuthenticated}
            />

            {campaignAutomation.crmProviderOption.value &&
              isCrmProviderAuthenticated && (
                <>
                  <div className="p-4 mb-6 border border-neutral-200 rounded-md bg-neutral-50">
                    <div className="flex items-center mb-4">
                      <SlidersHorizontal className="h-6 w-6 text-primary mr-2" />
                      <h4>Integration Settings</h4>
                    </div>
                    <div className="mb-4">
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={
                            !!campaignAutomation.recordCommunicationsInCrm
                          }
                          onChange={(e) =>
                            setCampaignAutomation({
                              ...campaignAutomation,
                              recordCommunicationsInCrm: e.target.checked,
                            })
                          }
                        />
                        <span className="text-sm font-medium text-neutral-600">
                          Record communications in CRM
                        </span>
                      </label>
                      <p className="text-xs text-neutral-400 ml-6">
                        Automatically log outreach attempts and call transcripts
                        to your CRM.
                      </p>
                    </div>
                  </div>
                </>
              )}
          </>
        )}
      </div>

      {/* Email Provider Configuration Section */}
      {campaignAutomation.followUpCommunications.some(
        (comm) => comm.contactVia === CommunicationChannel.EMAIL,
      ) && (
        <div className="border-t border-neutral-200 pt-6 mb-6">
          <div className="mb-4">
            <h3>Email Configuration</h3>
            <p className="text-sm text-neutral-400 mt-1">
              Configure the email provider used to send follow-up emails in this
              campaign.
            </p>
            <p className="text-sm text-warning-500 mt-1">
              Warning: Sending large volumes of emails may result in your domain
              being flagged as spam.
            </p>
          </div>

          <EmailProviderSelect
            onChange={handleEmailProviderChange}
            isAuthenticated={isValidEmailConfig}
          />
        </div>
      )}

      {campaignAutomation.followUpCommunications.some(
        (comm) => comm.contactVia === CommunicationChannel.SMS,
      ) && (
        <div className="border-t border-neutral-200 pt-6 mb-6">
          <div className="mb-4">
            <h3>SMS Configuration</h3>
            <p className="text-sm text-neutral-400 mt-1">
              Configure the SMS provider used to send follow-up SMS messages in
              this campaign.
            </p>
            <p className="text-sm text-warning-500 mt-1">
              Warning: Sending large volumes of SMS messages may result in your
              numbers being flagged as spam.
            </p>
          </div>

          <SmsProviderSelect
            onChange={handleSmsProviderChange}
            isAuthenticated={isValidSmsConfig}
          />
        </div>
      )}
      <div className="flex justify-between mt-6">
        <div>
          <Button
            type="button"
            variant="text"
            onClick={handleCancel}
            className="mr-2"
          >
            Cancel
          </Button>
        </div>

        <Can operation="create" subject={Subject.CAMPAIGNS}>
          <Button type="submit" onClick={handleSubmit} disabled={saveDisabled}>
            {savePending ? (
              <div className="flex gap-2 items-center justify-center">
                <Loading size="8" />
                <p className="text-neutral-500">Saving...</p>
              </div>
            ) : (
              'Save'
            )}
          </Button>
        </Can>
      </div>
    </>
  );
}
